import { _wq as $wq } from '@webqit/util/js/index.js';
import { _isObject, _isTypeObject } from '@webqit/util/js/index.js';
import { MessageEventPlus } from './MessageEventPlus.js';
import { WebSocketPort } from './WebSocketPort.js';
import { Observer } from '@webqit/observer';

export const _wq = (target, ...args) => $wq(target, 'port+', ...args);
export const _meta = (target, ...args) => $wq(target, 'port+', 'meta', ...args);
export const _options = (target) => $wq(target, 'port+', 'meta').get('options') || {};

const portPlusMethods = [
    'addEventListener',
    'addRequestListener',
    'postMessage',
    'postRequest',
    'dispatchEvent',
    'relay',
    'start',
    'readyStateChange',
    'removeEventListener',
    'close',
];
const portPlusProps = [
    'options',
    'readyState',
    'onmessage',
    'onmessageerror',
];

export class MessagePortPlus extends MessagePortPlusMixin(EventTarget) {

    constructor({ handshake = 0, postAwaitsOpen = false, ...options } = {}) {
        if (typeof handshake !== 'number') {
            throw new Error('handshake must be a number');
        }
        if (handshake < 0 || handshake > 2) {
            throw new Error('handshake must be between 0 and 2');
        }

        super();

        const portPlusMeta = _wq(this, 'meta');
        portPlusMeta.set('options', { handshake, postAwaitsOpen, ...options });
    }

    static [Symbol.hasInstance](instance) {
        // Direct subclass?
        if (Function.prototype[Symbol.hasInstance].call(this, instance)) return true;
        // Duct-type checking
        return portPlusMethods.every((m) => typeof instance[m] === 'function')
            || portPlusProps.every((m) => m in instance);
    }
}

export function MessagePortPlusMixin(superClass) {
    return class extends superClass {

        static upgradeInPlace(port, { handshake = 0, postAwaitsOpen = false } = {}) {
            if (port instanceof MessagePortPlus) {
                return port;
            }

            if (typeof handshake !== 'number') {
                throw new Error('handshake must be a number');
            }
            if (handshake < 0 || handshake > 2) {
                throw new Error('handshake must be between 0 and 2');
            }

            const proto = this.prototype;

            for (const prop of portPlusMethods.concat('_autoStart')) {
                const original = port[prop];
                const plus = proto[prop];

                if (original) Object.defineProperty(port, `_${prop}`, { value: original.bind(port), configurable: true });
                Object.defineProperty(port, prop, { value: plus.bind(port), configurable: true });
            }

            for (const prop of portPlusProps) {
                const original = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(port), prop);
                const plus = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(proto), prop);

                if (original) Object.defineProperty(port, `_${prop}`, { ...original, configurable: true });
                Object.defineProperty(port, prop, { ...plus, configurable: true });
            }

            const portPlusMeta = _wq(port, 'meta');
            portPlusMeta.set('options', { handshake, postAwaitsOpen });

            this.upgradeEvents(port);

            if (handshake === 0) port.start();

            return port;
        }

        static upgradeEvents(port, portPlus = null) {
            if (!portPlus) portPlus = port;

            const rawPortMeta = _meta(port);
            if (rawPortMeta.get('events+')) return; // Already wrapped

            const portPlusMeta = _meta(portPlus);
            const options = _options(portPlus);

            const garbageCollection = getGarbageCollection.call(portPlus);
            const readyStateInternals = getReadyStateInternals.call(portPlus);

            // ------------- SETUP

            if (port instanceof BroadcastChannel) {
                if (options.clientServerMode === 'server') {
                    portPlusMeta.set('clients', new Set);
                } else if (options.clientServerMode === 'client') {
                    portPlusMeta.set('client_id', `client-${(0 | Math.random() * 9e6).toString(36)}`);
                }
            }

            // ------------- MESSAGE

            const messageHandler = (e) => {
                if (e instanceof MessageEventPlus) return;
                // Stop propagation if the event for portPlus
                // since we'll repost it there
                if (port === portPlus) e.stopImmediatePropagation?.();
                // Hydrate event. Typically a WebSocket-specific feature
                e = this._hydrateMessage?.(portPlus, e) || e;

                /*
                 * Handle lifecycle events from the other end
                 */

                if (e.data?.ping === 'connect'
                    && typeof e.data['.wq']?.eventID === 'string'
                    && (!(port instanceof WebSocket) || options.handshake)) {
                    // This is a special ping from a MessagePort or BroadcastChannel instance
                    // that helps us simulate an "open" event
                    // If options.handshake !== 0, WebSockets too

                    const nowOpen = options.handshake === 0
                        || portPlusMeta.get('start.called');

                    const reply = { isOpen: nowOpen };

                    if (port instanceof BroadcastChannel) {
                        if (options.clientServerMode === 'server'
                            && typeof e.data.id === 'string') {
                            portPlusMeta.get('clients').add(e.data.id);
                            reply.id = 'server';
                        } else if (e.data.id === 'server'
                            && portPlusMeta.has('client_id')) {
                            reply.id = portPlusMeta.get('client_id');
                        }
                    }

                    e.ports?.forEach((p) => p.postMessage(reply));

                    if (nowOpen) {
                        portPlusMeta.set('remote.open.called', true);
                        portPlus.start();
                    }

                    return;
                }

                if (e.data?.ping === 'disconnect'
                    && typeof e.data['.wq']?.eventID === 'string') {
                    // This is a special ping from a BroadcastChannel instance
                    // that helps us simulate a "close" event
                    // WebSockets naturally fire a close event
                    // In nodejs only, MessagePort naturally fire a close event
                    // so we need to support simulated closing for when in the browser

                    const close = () => {
                        portPlusMeta.set('remote.close.called', true);
                        portPlus.close();
                    };

                    if (port instanceof BroadcastChannel) {
                        if (options.clientServerMode === 'server'
                            && typeof e.data.id === 'string') {
                            // Expel a client
                            portPlusMeta.get('clients').delete(e.data.id);
                            // Declare closed if no clients remain
                            if (!portPlusMeta.get('clients').size
                                && options.autoClose
                                && !readyStateInternals.close.state) {
                                close();
                            }
                        } else if (options.clientServerMode === 'client'
                            && e.data.id === 'server') {
                            // Server has closed
                            close();
                        }
                    } else if (port instanceof MessagePort) {
                        close();
                    }

                    return;
                }

                /*
                 * Do event rewrites and the Webflo live object magic
                 */

                let message = e.data;
                let wqOptions = {};
                if (typeof e.data?.['.wq']?.eventID === 'string') {
                    ({ message, ['.wq']: wqOptions } = e.data);
                }

                const eventPlus = new MessageEventPlus(message, {
                    target: portPlus,
                    ...wqOptions,
                    ports: e.ports,
                });

                portPlus.dispatchEvent(eventPlus);
            };

            const errorHandler = (e) => {
            };

            rawPortMeta.set('internal_call', true);
            port.addEventListener('message', messageHandler);
            port.addEventListener('error', errorHandler);
            rawPortMeta.delete('internal_call');

            garbageCollection.add(() => {
                port.removeEventListener('message', messageHandler);
                port.removeEventListener('error', errorHandler);

                rawPortMeta.set('events+', false);
            });

            rawPortMeta.set('events+', true);
        }

        get options() { return { ..._options(this) }; }

        // Messages

        get onmessageerror() {
            if (typeof super.onmessageerror !== 'undefined') {
                return super.onmessageerror;
            }
            if (typeof this._onmessageerror !== 'undefined') {
                return this._onmessageerror;
            }
            return null;
        }

        set onmessageerror(v) {
            if (typeof super.onmessageerror !== 'undefined') {
                super.onmessageerror = v;
                return;
            }

            if (v !== null && typeof v !== 'function') {
                throw new TypeError('onmessageerror must be a function');
            }

            if (Object.getOwnPropertyDescriptor(this, '_onmessageerror')?.set) {
                this._onmessageerror = v;
                return;
            }

            if (this._onmessageerror) this.removeEventListener('messageerror', this._onmessageerror);
            this.addEventListener('messageerror', v);
            this._onmessageerror = v;
        }

        get onmessage() {
            if (typeof super.onmessage !== 'undefined') {
                return super.onmessage;
            }
            if (typeof this._onmessage !== 'undefined') {
                return this._onmessage;
            }
            return null;
        }

        set onmessage(v) {
            if (typeof super.onmessage !== 'undefined') {
                super.onmessage = v;
                this._autoStart();
                return;
            }

            if (v !== null && typeof v !== 'function') {
                throw new TypeError('onmessage must be a function');
            }

            if (Object.getOwnPropertyDescriptor(this, '_onmessage')?.set) {
                this._onmessage = v;
                this._autoStart();
                return;
            }

            if (this._onmessage) this.removeEventListener('message', this._onmessage);
            this.addEventListener('message', v);
            this._onmessage = v;
            this._autoStart();
        }

        addEventListener(...args) {
            // Add to registry 
            const garbageCollection = getGarbageCollection.call(this);
            garbageCollection.add(() => {
                this._removeEventListener
                    ? this._removeEventListener(...args)
                    : super.removeEventListener(...args);
            });

            // Execute addEventListener()
            const returnValue = this._addEventListener
                ? this._addEventListener(...args)
                : super.addEventListener(...args);
            this._autoStart();
            return returnValue;
        }

        dispatchEvent(event) {
            if (event instanceof MessageEventPlus) {
                MessageEventPlus.setCurrentTarget(event, this);
            }

            const returnValue = this._dispatchEvent
                ? this._dispatchEvent(event)
                : super.dispatchEvent(event);

            if (event instanceof MessageEventPlus) {
                // Bubble semantics
                propagateEvent.call(this, event);
            }

            return returnValue;
        }

        postMessage(message, transferOrOptions = {}) {
            this._autoStart();

            let portPlusIgnore; // From starPort
            if (transferOrOptions?.portPlusIgnore) {
                ({ portPlusIgnore, ...transferOrOptions } = transferOrOptions);
            }

            // Update readyState
            const readyStateInternals = getReadyStateInternals.call(this);
            readyStateInternals.messaging.state = true;
            readyStateInternals.messaging.resolve(this);

            let _relayedFrom;

            if (!portPlusIgnore) {
                // Format payload if not yet in the ['.wq'] format
                const { portOptions, wqOptions: { relayedFrom, ...wqOptions } } = preProcessPostMessage.call(this, message, transferOrOptions);
                if (wqOptions.type !== 'message'
                    || wqOptions.live
                    || wqOptions.bubbles
                    || portOptions.transfer?.length && (this instanceof BroadcastChannel || this instanceof WebSocketPort)) {
                    message = { message, ['.wq']: wqOptions };
                }
                transferOrOptions = portOptions;
                _relayedFrom = relayedFrom;
            }

            // Exec
            const post = () => {
                this._postMessage
                    ? this._postMessage(message, transferOrOptions, _relayedFrom)
                    : super.postMessage(message, transferOrOptions);
            };

            // If client-server mode, wait for open ready state
            const options = _options(this);
            if (options.postAwaitsOpen) readyStateInternals.open.promise.then(post);
            else post();
        }

        // Requests

        addRequestListener(type, handler, options = {}) {
            const handle = async (e) => {
                const response = await handler(e);
                for (const port of e.ports) {
                    port.postMessage(response);
                }
            };
            this.addEventListener(type, handle, options);
        }

        postRequest(data, callback, options = {}) {
            let returnValue;

            if (_isObject(callback)) {
                options = { once: true, ...callback };
                returnValue = new Promise((resolve) => {
                    callback = resolve;
                });
            }

            const messageChannel = new MessageChannel;
            MessagePortPlus.upgradeInPlace(messageChannel.port1);
            messageChannel.port1.start();

            const { signal, once = false, transfer = [], ..._options } = options;

            const gc = () => {
                messageChannel.port1.close();
                messageChannel.port2.close();
            };
            signal?.addEventListener('abort', gc);

            messageChannel.port1.addEventListener('message', (e) => {
                callback(e);
                if (once) gc();
            }, { signal: signal || undefined/* NO NULLS */, once });

            this.postMessage(data, { ..._options, transfer: [messageChannel.port2].concat(transfer) });
            return returnValue;
        }

        // Forwarding

        relay({ to: targetPort, from: sourcePort, types = '*', channel = null, resolveMessage = null, bidirectional = false } = {}) {
            if (targetPort && sourcePort) {
                throw new Error('Only one of "to" and "from" may be specified');
            }

            if (sourcePort) {
                targetPort = this;
            } else sourcePort = this;

            if (targetPort === sourcePort) {
                throw new Error('Source and target ports cannot be the same');
            }

            if (!(sourcePort instanceof MessagePortPlus) || !(targetPort instanceof MessagePortPlus)) {
                throw new Error('Both source and target ports must be instance of MessagePortPlus.');
            }

            if (typeof types !== 'function' && !(types = [].concat(types)).length) {
                throw new Error('Event types must be specified.');
            }

            if (typeof channel === 'string') {
                channel = { from: channel };
            } else if (_isObject(channel)) {
                if (Object.keys(channel).filter((k) => !['from', 'to'].includes(k)).length) {
                    throw new Error('Channel must be a string or an object of "from"/"to" members');
                }
            } else if (channel) {
                throw new Error('Invalid channel parameter');
            }

            const downstreamRegistry = getDownstreamRegistry.call(sourcePort);
            const registration = { targetPort, types, channel, resolveMessage };
            downstreamRegistry.add(registration);

            let cleanup2;
            if (bidirectional) {
                cleanup2 = targetPort.relay(
                    { to: sourcePort, types, channel: channel && { to: channel.from, from: channel.to }, resolveMessage, bidirectional: false }
                );
            }

            return () => {
                downstreamRegistry.delete(registration);
                cleanup2?.();
            };
        }

        channel(channelSpec, resolveMessage = null, { handshake = 0, postAwaitsOpen = false } = {}) {
            const channel = new MessageChannel;

            MessagePortPlus.upgradeInPlace(channel.port1, { handshake, postAwaitsOpen });
            MessagePortPlus.upgradeInPlace(channel.port2, { handshake, postAwaitsOpen });

            const garbageCollection = getGarbageCollection.call(this);
            garbageCollection.add(this.relay({ channel: channelSpec, to: channel.port2, bidirectional: true, resolveMessage }));

            channel.port2.start();
            this.readyStateChange('close').then(() => {
                channel.port2.close();
            });

            return channel.port1;
        }

        projectMutations({ from, to, ...options }) {
            if (!from || !to
                || typeof from === 'string' && typeof to === 'string'
                || _isTypeObject(from) && _isTypeObject(to)) {
                throw new Error('Invalid "from"/"to" parameters');
            }
            if (typeof from === 'string') {
                if (!_isTypeObject(to)) throw new Error('Invalid "to" parameter. Object/array expected');
                return applyMutations.call(this, to, from, options);
            }
            if (typeof to === 'string') {
                if (!_isTypeObject(from)) throw new Error('Invalid "from" parameter. Object/array expected');
                return publishMutations.call(this, from, to, options);
            }
            throw new Error('Invalid "from"/"to" parameters');
        }

        // Lifecycle

        get readyState() {
            const readyStateInternals = getReadyStateInternals.call(this);
            return readyStateInternals.close.state ? 'closed'
                : (readyStateInternals.open.state ? 'open' : 'connecting');
        }

        readyStateChange(query) {
            if (!['open', 'messaging', 'close'].includes(query)) {
                throw new Error(`Invalid readyState query "${query}"`);
            }
            const readyStateInternals = getReadyStateInternals.call(this);
            return readyStateInternals[query].promise;
        }

        _autoStart() {
            const portPlusMeta = _meta(this);
            const options = _options(this);
            if (!portPlusMeta.get('internal_call')
                && options.handshake === 1) {
                this.start();
            }
        }

        start() {
            const readyStateInternals = getReadyStateInternals.call(this);
            if (readyStateInternals.open.state) return;

            const portPlusMeta = _meta(this);
            const options = _options(this);

            const readyStateOpen = (nowOpen) => {
                if (nowOpen) {
                    readyStateInternals.open.state = true;
                    readyStateInternals.open.resolve(this);
                }
                const handshakeChannel = portPlusMeta.get('handshake_channel');
                setTimeout(() => {
                    handshakeChannel?.port1.close();
                    handshakeChannel?.port2.close();
                    portPlusMeta.delete('handshake_channel');
                }, 100);
            };

            // A peer ping triggered us to open
            if (portPlusMeta.get('remote.open.called')) {
                readyStateOpen(true);
                return;
            }

            // Start was explicitly called
            if (portPlusMeta.get('start.called')) return;
            portPlusMeta.set('start.called', true);

            this._start
                ? this._start()
                : super.start?.();

            // No handshake?
            if (options.handshake === 0) {
                readyStateOpen(true);
                return;
            }

            const handshakeChannel = new MessageChannel;
            portPlusMeta.set('handshake_channel', handshakeChannel);

            handshakeChannel.port1.onmessage = (e) => {
                if (this instanceof BroadcastChannel
                    && options.clientServerMode === 'server'
                    && typeof e.data?.id === 'string') {
                    // Register clients that replied
                    portPlusMeta.get('clients').add(e.data.id);
                }
                if (typeof e.data?.isOpen) {
                    // This peer is ready to start messaging
                    readyStateOpen(true);
                } else readyStateOpen(false);
            };

            const { wqOptions } = preProcessPostMessage.call(this);
            const id = options.clientServerMode === 'server' ? 'server'
                : (options.clientServerMode === 'client' ? portPlusMeta.get('client_id') : null);
            const pingData = { ['.wq']: wqOptions, ping: 'connect', id };

            this._postMessage
                ? this._postMessage(pingData, { transfer: [handshakeChannel.port2] })
                : super.postMessage(pingData, { transfer: [handshakeChannel.port2] });
        }

        close(...args) {
            const readyStateInternals = getReadyStateInternals.call(this);

            if (readyStateInternals.close.state) return;
            readyStateInternals.close.state = true;

            const portPlusMeta = _meta(this);
            const options = _options(this);

            if (options.handshake > 0
                && !portPlusMeta.get('remote.close.called')
                && (this instanceof BroadcastChannel || this instanceof MessagePort)) {

                const { wqOptions } = preProcessPostMessage.call(this);
                const id = options.clientServerMode === 'server' ? 'server'
                    : (options.clientServerMode === 'client' ? portPlusMeta.get('client_id') : null);
                const pingData = { ['.wq']: wqOptions, ping: 'disconnect', id };

                this._postMessage
                    ? this._postMessage(pingData)
                    : super.postMessage(pingData);
            }

            // This should come before event:
            // Nodejs natively sends a close event to other port (port2) for MessagePort
            // this ensures:
            //  port2.ping() -> port2.close() -> port2.customCloseEvent()
            //  -> port1.customCloseEvent()
            this._close
                ? this._close(...args)
                : super.close(...args);

            readyStateInternals.close.resolve(this);

            garbageCollect.call(this);
        }
    };
}

export function MessagePortPlusMockPortsMixin(superClass) {
    return class extends MessagePortPlusMixin(superClass) {

        static _hydrateMessage(portPlus, event) {
            // But only one with ['.wq'].numPorts & ['.wq'].eventID is valid for port hydration
            if (typeof event.data?.['.wq']?.numPorts !== 'number' || typeof event.data['.wq'].eventID !== 'string') {
                return event;
            }

            const garbageCollection = getGarbageCollection.call(portPlus);
            const numPorts = event.data['.wq'].numPorts;
            Object.defineProperty(event, 'ports', { value: [], configurable: true });

            for (let i = 0; i < numPorts; i++) {
                const port = portPlus.channel(`${event.data['.wq'].eventID}:${i}`);
                event.ports.push(port);
            }

            return event;
        }

        _postMessage(payload, portOptions = {}) {
            const { transfer = [], ..._portOptions } = portOptions;

            if (typeof payload?.['.wq']?.eventID === 'string') {
                const garbageCollection = getGarbageCollection.call(this);

                const messagePorts = transfer.filter((t) => t instanceof MessagePort);
                const numPorts = messagePorts.length;

                for (let i = 0; i < numPorts; i++) {
                    MessagePortPlus.upgradeInPlace(messagePorts[i]);

                    garbageCollection.add(this.relay({ channel: `${payload['.wq'].eventID}:${i}`, to: messagePorts[i], bidirectional: true }));

                    messagePorts[i].start();
                    messagePorts[i].readyStateChange('close').then(() => {
                        // Send a disconnect ping to port2
                        const { wqOptions } = preProcessPostMessage.call(this);
                        const pingData = { ['.wq']: wqOptions, ping: 'disconnect' };
                        this.postMessage(pingData, { type: `${payload['.wq'].eventID}:${i}:message` });
                    });
                }

                payload['.wq'].numPorts = numPorts; // IMPORTANT: numPorts must be set before ports are added
            }

            return this.__postMessage(payload, _portOptions);
        }
    };
}

export function propagateEvent(event) {
    if (event.propagationStopped) return;
    const portMeta = _meta(this);

    if (portMeta.get('parentPort') instanceof EventTarget && (
        event.bubbles
        || portMeta.get('parentPort')?.findPort?.((port) => port === this) && event instanceof MessageEventPlus)
    ) {
        portMeta.get('parentPort').dispatchEvent(event);
    }

    const downstreamRegistry = getDownstreamRegistry.call(this);
    if (!downstreamRegistry.size) return;

    if (event instanceof MessageEventPlus) {
        const { type, eventID, data, live, bubbles, ports } = event;

        const called = new WeakSet;
        for (const { targetPort, types, channel, resolveMessage } of downstreamRegistry) {
            if (called.has(targetPort)) continue;

            let $type = type;
            if (channel.from) {
                [, $type] = (new RegExp(`^${channel.from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:([^:]+)$`)).exec(type) || [];
                if (!$type) continue;
            }

            const matches = typeof types === 'function'
                ? types($type, this, targetPort, channel)
                : [].concat(types).find((t) => {
                    return t === $type || t === '*';
                });

            if (!matches) continue;
            called.add(targetPort);

            targetPort.postMessage(resolveMessage ? resolveMessage(data, this, targetPort, channel) : data, {
                transfer: ports,
                type: channel.to ? `${channel.to}:${$type}` : $type,
                eventID,
                bubbles,
                live,
                forwarded: true,
                relayedFrom: this
                // "relayedFrom" is typically a RelayPort feature
                // in case targetPort is a RelayPort
            });
        }
    }
}

export function getGarbageCollection() {
    const portPlusMeta = _meta(this);
    if (!portPlusMeta.has('garbage_collection')) {
        portPlusMeta.set('garbage_collection', new Set);
    }
    return portPlusMeta.get('garbage_collection');
}

export function getDownstreamRegistry() {
    const portPlusMeta = _meta(this);
    if (!portPlusMeta.has('downstream_registry')) {
        portPlusMeta.set('downstream_registry', new Set);
    }
    return portPlusMeta.get('downstream_registry');
}

export function getReadyStateInternals() {
    const portPlusMeta = _meta(this);
    if (!portPlusMeta.has('readystate_registry')) {
        const $ref = (o) => {
            o.promise = new Promise((res, rej) => (o.resolve = res, o.reject = rej));
            return o;
        };
        portPlusMeta.set('readystate_registry', {
            open: $ref({}),
            messaging: $ref({}),
            close: $ref({}),
        });
    }
    return portPlusMeta.get('readystate_registry');
}

export function garbageCollect() {
    const portPlusMeta = _meta(this);

    for (const dispose of portPlusMeta.get('garbage_collection') || []) {
        if (dispose instanceof AbortController) {
            dispose.abort();
        } else if (typeof dispose === 'function') dispose();
    }
    portPlusMeta.get('garbage_collection')?.clear();
    portPlusMeta.get('downstream_registry')?.clear();
}

export function preProcessPostMessage(message = undefined, transferOrOptions = {}) {
    if (Array.isArray(transferOrOptions)) {
        transferOrOptions = { transfer: transferOrOptions };
    } else if (!transferOrOptions
        || typeof transferOrOptions !== 'object') {
        throw new TypeError('transferOrOptions must be an array or an object');
    }

    let {
        type = 'message',
        eventID = null,
        live = false,
        observing = false,
        bubbles = false,
        forwarded = false,
        relayedFrom = null,
        signal = undefined,
        withArrayMethodDescriptors = false,
        honourDoneMutationFlags = false,
        ...portOptions
    } = transferOrOptions;

    if (!eventID) eventID = `${type}-${(0 | Math.random() * 9e6).toString(36)}`;

    if (!observing && !forwarded && _isTypeObject(message) && live && !type?.endsWith('.mutate')) {
        publishMutations.call(this, message, eventID, { signal: signal || undefined/* NO NULLS */, withArrayMethodDescriptors, honourDoneMutationFlags });
        observing = true;
    }

    return {
        portOptions,
        wqOptions: {
            type,
            eventID,
            live,
            observing,
            honourDoneMutationFlags,
            bubbles,
            forwarded,
            relayedFrom
        }
    };
}

export function publishMutations(message, eventID, { signal, withArrayMethodDescriptors = true, honourDoneMutationFlags = false } = {}) {
    if (!_isTypeObject(message)) throw new TypeError('data must be a plain object and not a stream');
    if (typeof eventID !== 'string') throw new TypeError('eventID must be a non-empty string');

    const mutationHandler = (mutations) => {
        let mutationsDone;

        if (withArrayMethodDescriptors
            && Array.isArray(mutations[0].target)
            && !mutations[0].argumentsList
            && !['set', 'defineProperty', 'deleteProperty'].includes(mutations[0].operation)) return;

        this.postMessage(
            mutations.map((m) => {
                mutationsDone = !mutationsDone
                    && honourDoneMutationFlags
                    && m.detail?.done;
                return { ...m, target: undefined };
            }),
            { type: `${eventID}.mutate` }
        );

        if (mutationsDone) dispose.abort();
    };


    const dispose = Observer.observe(message, Observer.subtree(), mutationHandler, { signal: signal || undefined/* NO NULLS */, withArrayMethodDescriptors });
    const garbageCollection = getGarbageCollection.call(this);
    garbageCollection.add(dispose);

    return dispose;
}

export function applyMutations(message, eventID, { signal, honourDoneMutationFlags = false } = {}) {
    if (!_isTypeObject(message)) throw new TypeError('data must be a plain object and not a stream');
    if (typeof eventID !== 'string') throw new TypeError('eventID must be a non-empty string');

    const messageHandler = (e) => {
        if (!e.data?.length) return;

        let mutationsDone;

        Observer.batch(message, () => {
            for (const mutation of e.data) {
                mutationsDone = !mutationsDone
                    && honourDoneMutationFlags
                    && mutation.detail?.done;

                if (mutation.argumentsList) {
                    const target = !mutation.path.length ? message : Observer.get(message, Observer.path(...mutation.path));
                    Observer.proxy(target)[mutation.operation](...mutation.argumentsList);
                    continue;
                }

                if (mutation.key !== 'length'
                    || ['set', 'defineProperty', 'deleteProperty'].includes(mutation.operation)) {
                    const target = mutation.path.length === 1 ? message : Observer.get(message, Observer.path(...mutation.path.slice(0, -1)));
                    if (mutation.type === 'delete') {
                        Observer.deleteProperty(target, mutation.key);
                    } else {
                        Observer.set(target, mutation.key, mutation.value);
                    }
                }
            }
        });

        if (mutationsDone) cleanup();
    };

    this.addEventListener(`${eventID}.mutate`, messageHandler, { signal: signal || undefined/* NO NULLS */ });
    const cleanup = () => this.removeEventListener(`${eventID}.mutate`, messageHandler);

    const garbageCollection = getGarbageCollection.call(this);
    garbageCollection.add(cleanup);

    return cleanup;
}